import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { Icon } from "@material-ui/core";
import { presentations as presentationsApi } from "apis/callables";
import { GetSlidesMetadataResponse } from "apis/types";
import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import PresentationTemplatesPaneController, { PresentationTemplatesPaneControllerState } from "js/controllers/PresentationTemplatesPaneController";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import { t } from "js/i18n";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexBox } from "js/react/components/LayoutGrid";
import Loadable from "js/react/components/Loadable";
import { NoMatchNotice, NoTeamTemplateNotice } from "js/react/components/Notice";
import { BlueButton, UIPane } from "js/react/components/UiComponents";
import {
    UIPaneResultsContainer,
    UIPaneResultsInnerContainer
} from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { TeamResourcesHeaderFilter, TeamResourcesPaneHeader } from "js/react/views/TeamResources/components/TeamResourcesPaneHeader";

import { PresentationTemplateItem, PresentationTemplateItemSkeleton } from "./PresentationTemplateItem";

const SEARCH_FIELD_ALL = "__all__";
const TAG_FILTER_ALL = "__all__";

const SEARCH_FIELD_OPTIONS = [
    {
        id: SEARCH_FIELD_ALL,
        name: "All Content"
    },
    {
        id: "slide:text",
        name: "Slide Text"
    },
    {
        id: "resource:metadata:name",
        name: "Name"
    },
    {
        id: "resource:metadata:description",
        name: "Description"
    },
    {
        id: "resource:tagsText",
        name: "Tags"
    }
];

const SORT_OPTIONS = [
    {
        value: "resource.metadata.name",
        label: "Name",
        icon: <Icon>sort_by_alpha</Icon>
    },
    {
        value: "resource.metadata.ratedByUsersCount",
        label: "Rating",
        reverse: true,
        icon: <Icon>star</Icon>
    },
    {
        value: "resource.createdAt",
        label: "Recently Created",
        reverse: true,
        icon: <Icon>date_range</Icon>
    },
    {
        value: "document.modifiedAt",
        label: "Recently Modified",
        reverse: true,
        icon: <Icon>update</Icon>
    },
];

const StyledUIPane = styled(UIPane as any)`
    padding: 24px 20px 0;
    display: flex;
    flex-direction: column;
`;

const StyledResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
`;

const StyledResultsInnerContainer = styled(UIPaneResultsInnerContainer)`
    position: static;
    left: 0;
    right: 0;
`;

export interface PresentationTemplatesListProps extends PresentationTemplatesPaneControllerState {
    editable: boolean;
    onSelect?: (presentationId: string | null, isDoubleClick?: boolean) => void;
}

const PresentationTemplatesList = PresentationTemplatesPaneController.withState(function PresentationTemplatesList(props: PresentationTemplatesListProps) {
    const {
        editable,
        onSelect,
        data,
        filters,
        search,
        fetching
    } = props;
    const [selectedPresentationId, setSelectedPresentationId] = useState<string | null>(null);
    const presentationsSlidesMetadataRef = useRef<Record<string, Promise<GetSlidesMetadataResponse>>>({});

    const handleGetPresentationSlidesMetadata = async (presentationId: string) => {
        if (!presentationsSlidesMetadataRef.current[presentationId]) {
            presentationsSlidesMetadataRef.current[presentationId] = presentationsApi.getSlidesMetadata({ id: presentationId });
        }
        return presentationsSlidesMetadataRef.current[presentationId];
    };

    const handleSelect = (presentationId: string | null, isDoubleClick?: boolean) => {
        if (!editable) {
            setSelectedPresentationId(presentationId);
        }
        onSelect?.(presentationId, isDoubleClick);
    };

    const handleVisibleChange = (presentationId: string, isVisible: boolean) => {
        if (!isVisible || !PresentationTemplatesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(record => record.document.id === presentationId);
        if (index === data.length - 1) {
            PresentationTemplatesPaneController.loadMore();
        }
    };

    useEffect(() => {
        if (!selectedPresentationId) {
            return;
        }

        if (!data.some(record => record.document.id === selectedPresentationId)) {
            handleSelect(null, false);
        }
    }, [getObjectHash(data)]);

    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const hasFilter = filters.some(filter => filter.field !== "resource.metadata.isPublished") || !!search.query;
    const hasNoMatch = data.length === 0 && hasFilter;
    const hasNoData = data.length === 0 && !hasFilter;

    const isLoadingMore = fetching && data.length > 0;
    const isLoadingInitial = fetching && data.length === 0;

    return (
        <StyledResultsContainer style={canUpgradeToManageSharedResources ? { paddingTop: "20px" } : {}}>
            <StyledResultsInnerContainer>
                {isLoadingInitial && <FetchingClickShield visible backgroundColor="#EEEEEE" />}
                {hasNoMatch && <NoMatchNotice />}
                {hasNoData && <NoTeamTemplateNotice />}
                {data.map(record => (
                    <PresentationTemplateItem
                        key={record.document.id}
                        record={record}
                        editable={editable}
                        onSelect={handleSelect}
                        selected={selectedPresentationId === record.document.id}
                        getPresentationSlidesMetadata={handleGetPresentationSlidesMetadata}
                        onVisibleChange={isVisible => handleVisibleChange(record.document.id, isVisible)}
                    />
                ))}
                {isLoadingMore && <PresentationTemplateItemSkeleton />}
            </StyledResultsInnerContainer>
        </StyledResultsContainer>
    );
});

export interface PresentationTemplatesPaneContentsProps extends PresentationTemplatesPaneControllerState {
    editable: boolean;
    onSelect?: (presentationId: string | null, isDoubleClick?: boolean) => void;
}

const PresentationTemplatesPaneContents = PresentationTemplatesPaneController.withState(function PresentationTemplatesPaneContents(props: PresentationTemplatesPaneContentsProps) {
    const {
        editable,
        onSelect,
        tags,
        sort,
        search,
        filters
    } = props;

    const handleTagFilterChange = (tagFilter: string) => {
        const baseFilters = filters.filter(filter => filter.field === "resource.metadata.isPublished");

        if (tagFilter === TAG_FILTER_ALL) {
            PresentationTemplatesPaneController.setFilters(baseFilters);
            return;
        }

        PresentationTemplatesPaneController.setFilters([...baseFilters, {
            field: "resource.tags",
            value: tagFilter,
            operator: "eq"
        }]);
    };

    const handleSearchChange = (searchTerm: string) => {
        PresentationTemplatesPaneController.setSearch({
            query: searchTerm?.trim(),
            fields: search.fields
        });
    };

    const handleSortChange = (nextSort: { field: string, reverse: boolean }) => {
        PresentationTemplatesPaneController.setSort({
            field: nextSort.field,
            direction: nextSort.reverse ? "desc" : "asc"
        });
    };

    const handleSearchFieldChange = (searchField: string) => {
        PresentationTemplatesPaneController.setSearch({
            query: search.query,
            fields: searchField === SEARCH_FIELD_ALL ? undefined : [searchField]
        });
    };

    const handleAddItem = () => {
        AppController.showCreatePresentationDialog({
            pane: CreatePresentationDialogPaneType.BLANK_PRESENTATION,
            isTeamTemplate: true
        });
    };

    const searchFieldOptions = useMemo(() => SEARCH_FIELD_OPTIONS.map(option => ({
        ...option,
        name: t(option.name)
    })), [t]);

    const sortOptions = useMemo(() => SORT_OPTIONS.map(option => ({
        ...option,
        label: t(option.label)
    })), [t]);

    const tagOptions = [
        {
            id: TAG_FILTER_ALL,
            name: t("All Tags"),
        },
        ...tags.map(({ tag, count }) => ({
            id: tag,
            name: `${tag} (${count})`,
        })),
    ];

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToCreateSharedResources = (WorkspaceController.plan.tier !== WorkspacePlanTier.BASIC) && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.CREATE);
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const tagFilter = filters.find(filter => filter.field === "resource.tags");
    const tagFilterValue = tagFilter ? tagFilter.value : TAG_FILTER_ALL;

    return (
        <StyledUIPane>
            {(canUseSharedResources || canUpgradeToManageSharedResources) && (
                <TeamResourcesPaneHeader
                    title={t("Presentation Templates")}
                    action={canUseSharedResources && !canUpgradeToCreateSharedResources && editable ? (
                        <BlueButton onClick={handleAddItem}>
                            {t("Create Presentation Template")}
                        </BlueButton>
                    ) : null}
                    onSearch={canUseSharedResources ? handleSearchChange : null}
                    searchPlaceholder={canUseSharedResources ? t("Search team templates") : ""}
                    filterContent={canUseSharedResources ? (
                        <>
                            <TeamResourcesHeaderFilter
                                value={tagFilterValue}
                                onChange={event => handleTagFilterChange(event.target.value)}
                                options={tagOptions}
                            />
                            <TeamResourcesHeaderFilter
                                value={search.fields?.[0] ?? SEARCH_FIELD_ALL}
                                onChange={event => handleSearchFieldChange(event.target.value)}
                                options={searchFieldOptions}
                            />
                        </>
                    ) : null}
                    showControls={canUseSharedResources}
                    sort={canUseSharedResources ? sortOptions.find(option => option.value === sort?.field) : null}
                    sortOptions={canUseSharedResources ? sortOptions : []}
                    onSetSort={canUseSharedResources ? ({ field, reverse }) => {
                        handleSortChange({ field, reverse: !!reverse });
                    } : null}
                />
            )}
            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth padding="0px 0px 20px 0px">
                    <Banner
                        headerText={t("Unlock Team Presentation Templates")}
                        bodyText={t("Upgrade to a Team plan to give your team a head start with a library of presentation templates.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_templates" }
                            })}
                    />
                </FlexBox>
            )}
            <PresentationTemplatesList
                editable={editable}
                onSelect={onSelect}
            />
        </StyledUIPane>
    );
});

export interface PresentationTemplatesPaneProps {
    onReady?: () => void;
    onSelect?: (presentationId: string | null, isDoubleClick?: boolean) => void;
    editable: boolean;
}

export const PresentationTemplatesPane = WorkspaceController.withInitializedState(PresentationTemplatesPaneController.withState(
    function PresentationTemplatesPane(props: PresentationTemplatesPaneProps & PresentationTemplatesPaneControllerState) {
        const {
            editable,
            initialized,
            onSelect,
            onReady
        } = props;

        useEffect(() => {
            PresentationTemplatesPaneController.initialize({
                resourceTypes: [WorkspaceResourceType.PRESENTATION_TEMPLATE],
                workspaceId: WorkspaceController.workspaceId,
                sort: {
                    field: "document.modifiedAt",
                    direction: "desc"
                },
                filters: !editable ? [{
                    field: "resource.metadata.isPublished",
                    value: true,
                    operator: "eq"
                }] : []
            });

            return () => {
                PresentationTemplatesPaneController.reset();
            };
        }, [editable]);

        return (
            <Loadable isLoading={!initialized}>
                {onReady && typeof onReady === "function" &&
                    <OnMountedReporter onMounted={onReady} />
                }
                <PresentationTemplatesPaneContents
                    editable={editable}
                    onSelect={onSelect}
                />
            </Loadable>
        );
    }
));
